import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';

import defaultSettings from '@wix/wix-vod-shared/src/app-settings/default-settings';
import { COMPACT_SHOW_VIDEO_LIST_VALUES } from '@wix/wix-vod-shared/src/app-settings/constants/compact-show-video-list';
import { COMPACT_PLAY_TYPE_VALUES } from '@wix/wix-vod-shared/src/app-settings/constants/compact-play-type';
import { MAIN_ACTION_STYLE_TYPES } from '@wix/wix-vod-shared/src/app-settings/constants/main-action-style-types';
import { MAIN_ITEM_TYPE_VALUES } from '@wix/wix-vod-shared/src/app-settings/constants/main-item-type-values';
import { LAYOUT_STYLE_VALUES } from '@wix/wix-vod-shared/src/app-settings/constants/layout-style-values';
import { CHANNEL_LAYOUT_VALUES } from '@wix/wix-vod-shared/src/app-settings/constants/channel-layout-values';
import { SHOW_INFO_VALUES } from '@wix/wix-vod-shared/src/app-settings/constants/show-info-values';
import { INFO_POSITION_VALUES } from '@wix/wix-vod-shared/src/app-settings/constants/info-position-values';
import { SHOW_EXPANDED_VIDEO_LIST_INFO_VALUES } from '@wix/wix-vod-shared/src/app-settings/constants/show-expanded-video-list-titles';
import WixSDK from '../utils/wix-sdk';
import { getChannelsGroupedByIds, getChannelById } from '@wix/wix-vod-shared/src/common/selectors/channels';
import channelTypes from 'shared/constants/channel-types';


export function getAllSettings(state) {
  return state.appSettings;
}

export function getEditMode(state) {
  return _.get(state, 'appSettings.editMode');
}

export function getNumberOfRows(state) {
  return _.get(state, 'appSettings.numbers.numberOfRows', defaultSettings.numbers.numberOfRows);
}

export function isNumberOfRowsForClassicEnabled(state) {
  return _.get(state, 'appSettings.booleans.experimentNumbersOfRowsClassic', false);
}

export function getVideosInRowCount(state) {
  return _.get(state, 'appSettings.numbers.videosInRow', defaultSettings.numbers.videosInRow);
}

export function getVideoInfoPosition(state) {
  return _.get(state, 'appSettings.numbers.videoInfoPosition', defaultSettings.numbers.videoInfoPosition);
}

export function getInfoActionVisibility(state) {
  return _.get(state, 'appSettings.numbers.showInfoAction', defaultSettings.numbers.showInfoAction);
}

export function getExpandedViewInfoActionVisibility(state) {
  return _.get(state, 'appSettings.numbers.showInfoAction', defaultSettings.numbers.showInfoAction);
}

export function getLayoutStyle(state) {
  return _.get(state, 'appSettings.numbers.layoutStyle', defaultSettings.numbers.layoutStyle);
}

export function getExpandedViewTheme(state) {
  return _.get(state, 'appSettings.numbers.expandedTheme', defaultSettings.numbers.expandedTheme);
}

export function getSiteUrl(state) {
  return _.get(state, 'appSettings.siteUrl');
}

export function getCompId(state) {
  return _.get(state, 'appSettings.compId');
}

export function getCurrentPageId(state) {
  return _.get(state, 'appSettings.sitePageId');
}

export function getInstance(state) {
  return _.get(state, 'appSettings.instance');
}

export function getInstanceId(state) {
  return _.get(state, 'appSettings.instanceId');
}

export function getPlayType(state) {
  return _.get(state, 'appSettings.numbers.playType', defaultSettings.numbers.playType);
}

export function getCompactMainActionStyleType(state) {
  return _.get(state, 'appSettings.numbers.mainActionStyleType', defaultSettings.numbers.mainActionStyleType);
}

export function getChannelLayout(state) {
  return _.get(state, 'appSettings.numbers.channelLayout', defaultSettings.numbers.channelLayout);
}

export function getMainItemOverlayOpacity(state) {
  return _.get(state, 'appSettings.numbers.mainItemOverlayOpacity', defaultSettings.numbers.mainItemOverlayOpacity);
}

export function getVideoListVisibility(state) {
  return _.get(state, 'appSettings.numbers.videoListVisibility', defaultSettings.numbers.videoListVisibility);
}

export function getCompactShowVideoListType(state) {
  return _.get(state, 'appSettings.numbers.compactShowVideoListType', defaultSettings.numbers.compactShowVideoListType);
}

export function getShowSearch(state) {
  return _.get(state, 'appSettings.booleans.showSearch', defaultSettings.booleans.showSearch);
}

export const isCompactLayout = createSelector(
  getChannelLayout,
  channelLayout => channelLayout === CHANNEL_LAYOUT_VALUES.COMPACT
);

export const isStripLayout = createSelector(
  getChannelLayout,
  channelLayout => channelLayout === CHANNEL_LAYOUT_VALUES.STRIP
);

export const isClassicLayout = createSelector(
  getChannelLayout,
  channelLayout => channelLayout === CHANNEL_LAYOUT_VALUES.CLASSIC
);

export const canShowSearch = createSelector(
  getShowSearch,
  isCompactLayout,
  (isSearchVisible, isCompact) => isSearchVisible && !isCompact
);

export function canShowMainItemTitle(state) {
  return _.get(state, 'appSettings.booleans.showTitleForMainItem', defaultSettings.booleans.showTitleForMainItem);
}

export function canShowActionBar(state) {
  return _.get(state, 'appSettings.booleans.showActionBar', defaultSettings.booleans.showActionBar);
}

export function getCanShowChannelInfo(state) {
  return _.get(state, 'appSettings.booleans.showChannelInfo', defaultSettings.booleans.showChannelInfo);
}

export const canShowChannelInfo = createSelector(
  isCompactLayout,
  canShowActionBar,
  getCanShowChannelInfo,
  (isCompact, isActionBarVisible, isChannelInfoVisible) => {
    if (isCompact) {
      return isActionBarVisible && isChannelInfoVisible;
    }
    return isChannelInfoVisible;
  }
);

export function getCanShowChannelShare(state) {
  return _.get(state, 'appSettings.booleans.showChannelShare', defaultSettings.booleans.showChannelShare);
}

export const canShowChannelShare = createSelector(
  isCompactLayout,
  canShowActionBar,
  getCanShowChannelShare,
  isSingleVideo,
  (isCompact, isActionBarVisible, isChannelShareVisible, isSingleVideo) => {
    if (isSingleVideo) {
      return false;
    }
    if (isCompact) {
      return isActionBarVisible && isChannelShareVisible;
    }
    return isChannelShareVisible;
  }
);

export function getCanShowSignIn(state) {
  return _.get(state, 'appSettings.booleans.showSignIn', defaultSettings.booleans.showSignIn);
}

export const canShowSignIn = createSelector(
  isCompactLayout,
  canShowActionBar,
  getCanShowSignIn,
  (isCompact, isActionBarVisible, isSignInVisible) => {
    if (isCompact) {
      return isActionBarVisible && isSignInVisible;
    }
    return isSignInVisible;
  }
);

export const canShowVideoList = createSelector(
  getCompactShowVideoListType,
  compactShowVideoListType => compactShowVideoListType !== COMPACT_SHOW_VIDEO_LIST_VALUES.NEVER_SHOW
);

export function getShowPublisherForMainItem(state) {
  return _.get(state, 'appSettings.booleans.showPublisherForMainItem', defaultSettings.booleans.showPublisherForMainItem);
}

export const canShowMainItemPublisher = createSelector(
  getShowPublisherForMainItem,
  isCompactLayout,
  (isPublisherVisible, isCompact) => isPublisherVisible && !isCompact
);

export function isPreviewHover(state) {
  return _.get(state, 'appSettings.booleans.previewHover', false);
}

export function getVideoListPublisherVisibility(state) {
  return _.get(state, 'appSettings.booleans.showVideoPublisherInList', defaultSettings.booleans.showVideoPublisherInList);
}

export const canShowVideoListItemPublisher = createSelector(
  getVideoListPublisherVisibility,
  isCompactLayout,
  (isPublisherVisible, isCompact) => isPublisherVisible && !isCompact
);

export function getChannelIdFromAppSettings(state) {
  return _.get(state, 'appSettings.fonts.channelId');
}

export function getVideoIdFromAppSettings(state) {
  return _.get(state, 'appSettings.fonts.videoId');
}

export function isYoutubeFeed(state) {
  const channelsData = getChannelsGroupedByIds(state);
  const channelId = getChannelIdFromAppSettings(state);
  const data = channelsData[channelId];
  return data && data.channelType === channelTypes.YOUTUBE_FEED;
}

export function getLanguageFromAppSettings(state) {
  return (
    _.get(state, 'appSettings.fonts.language') ||
    _.get(window, '__Locale.locale') ||
    WixSDK.Utils.getLocale()
  );
}

export function canShowVideoListItemTitle(state) {
  return _.get(state, 'appSettings.booleans.showVideoTitleInList', defaultSettings.booleans.showVideoTitleInList);
}

export function canShowVideoTitle(state) {
  return _.get(state, 'appSettings.booleans.showVideoTitle', defaultSettings.booleans.showVideoTitle);
}

export function canShowFullInfo(state) {
  return _.get(state, 'appSettings.booleans.showFullInfo', defaultSettings.booleans.showFullInfo);
}

export function canShareVideo(state) {
  return _.get(state, 'appSettings.booleans.enableVideosSharing', defaultSettings.booleans.enableVideosSharing);
}

export const canShowChannelTags = createSelector(
  [
    canShowSearch,
    state => _.get(state, 'appSettings.booleans.showChannelTags', defaultSettings.booleans.showChannelTags),
  ],
  (canShowSearch, canShowTags) => canShowSearch && canShowTags,
);

export function canShowExpandedVideoInfo(state) {
  return _.get(state, 'appSettings.booleans.showExpandedVideoInfo', defaultSettings.booleans.showExpandedVideoInfo);
}

export function canShowExpandedVideoShare(state) {
  return _.get(state, 'appSettings.booleans.enableExpandedVideosSharing', defaultSettings.booleans.enableExpandedVideosSharing);
}

export function canShowExpandedSignIn(state) {
  return _.get(state, 'appSettings.booleans.showSignIn', defaultSettings.booleans.showSignIn);
}

export function canShowExpandedChannelTitle(state) {
  return _.get(state, 'appSettings.booleans.showExpandedChannelTitle', defaultSettings.booleans.showExpandedChannelTitle);
}

export function canShowExpandedVideoTitle(state) {
  return _.get(state, 'appSettings.booleans.showExpandedVideoTitle', defaultSettings.booleans.showExpandedVideoTitle);
}

export function canShowExpandedVideoList(state) {
  return _.get(state, 'appSettings.booleans.showExpandedVideoList', defaultSettings.booleans.showExpandedVideoList);
}

export function canShowExpandedChannelShare(state) {
  return getCanShowChannelShare(state);
}

export function getMainItemType(state) {
  return _.get(state, 'appSettings.numbers.mainItemType', defaultSettings.numbers.mainItemType);
}

export const isMainItemFeaturedVideo = createSelector(
  [
    getMainItemType,
  ],
  mainItemType => mainItemType === MAIN_ITEM_TYPE_VALUES.FEATURED_VIDEO,
);

export const areElementsRounded = createSelector(
  [
    getLayoutStyle,
  ],
  layoutStyle => layoutStyle === LAYOUT_STYLE_VALUES.ROUND,
);

export const isCompactPlayInFrame = createSelector(
  [
    getPlayType,
  ],
  playType => playType === COMPACT_PLAY_TYPE_VALUES.IN_FRAME,
);

export const isCompactMainActionsOnlyIcons = createSelector(
  [
    getCompactMainActionStyleType,
  ],
  styleType => styleType === MAIN_ACTION_STYLE_TYPES.ICON,
);

export const isVideosListInfoAlwaysShown = createSelector(
  [
    getInfoActionVisibility,
  ],
  visibilityType => visibilityType === SHOW_INFO_VALUES.ALWAYS,
);

export const isVideosListInfoNeverShown = createSelector(
  [
    getInfoActionVisibility,
  ],
  visibilityType => visibilityType === SHOW_INFO_VALUES.NEVER,
);

export const isVideosListInfoPositionBelow = createSelector(
  [
    getVideoInfoPosition,
  ],
  videoInfoPosition => videoInfoPosition === INFO_POSITION_VALUES.BELOW_VIDEOS,
);

export const isVideosListInfoShownOnHover = createSelector(
  [
    getInfoActionVisibility,
  ],
  visibilityType => visibilityType === SHOW_INFO_VALUES.ON_MOUSE_OVER,
);

export const isExpandedModeVideosListInfoAlwaysShown = createSelector(
  [
    getExpandedViewInfoActionVisibility,
  ],
  visibilityType => visibilityType === SHOW_EXPANDED_VIDEO_LIST_INFO_VALUES.ALWAYS,
);

export const isExpandedModeVideosListInfoShownOnHover = createSelector(
  [
    getExpandedViewInfoActionVisibility,
  ],
  visibilityType => visibilityType === SHOW_EXPANDED_VIDEO_LIST_INFO_VALUES.ON_MOUSE_OVER,
);

export const isInfoActionDisabled = createSelector([
    isClassicLayout,
    isCompactLayout,
    isStripLayout,
    canShowActionBar,
    getCompactShowVideoListType,
    getVideoInfoPosition,
    getVideoListVisibility,
  ], (
    isClassicLayout,
    isCompactLayout,
    isStripLayout,
    isActionBarVisible,
    compactShowVideoListType,
    videoInfoPosition,
    videoListVisibility,
  ) => {
    if (isCompactLayout) {
      return !isActionBarVisible || compactShowVideoListType === COMPACT_SHOW_VIDEO_LIST_VALUES.NEVER_SHOW;
    }
    if (isClassicLayout) {
      return videoInfoPosition === INFO_POSITION_VALUES.BELOW_VIDEOS;
    }
    if (isStripLayout) {
      return videoListVisibility === SHOW_INFO_VALUES.NEVER;
    }
  }
);

export function isSingleVideo(state) {
  const singleVideoId = _.get(state, 'appSettings.fonts.videoId', '');
  return !isEmpty(singleVideoId);
}

export function getChannelDefaultVideoId(state) {

  if (isSingleVideo(state)) {
    return getVideoIdFromAppSettings(state);
  }

  const channelId = getChannelIdFromAppSettings(state);
  const channel = getChannelById(state, channelId);
  return (channel && channel.featuredId) || state.channelDefaultVideoId;
}

export function getChannelFeaturedId(state) {
  const channelId = getChannelIdFromAppSettings(state);
  return _.get(state, ['entities', 'channels', channelId, 'featuredId']);
}



// WEBPACK FOOTER //
// ./src/shared/selectors/app-settings.js